import Heading from '../../components/shared/heading';
import React, { useContext, useEffect, useState } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import { prependDefault } from '../../utils/structure-options-utils';
import { getAllVehicleModels, updateVehicle, getAllVehicleMakes } from '../../services/vehicles-service';
import { AccountsContext } from '../../components/context/account-context';
import Loader from '../../components/shared/loader';
import { camelCaseToWords } from '../../utils/structure-words-utils';

const EditModal = ({ selectedRow, setSelectedRow, setIsEditModalOpen, setData, vehicleTypes, vehicleBodies, vehicleMakes, globalDataLoading }) => {
    const { accountId } = useContext(AccountsContext);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [loadingFields, setLoadingFields] = useState({});
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [localMakes, setLocalMakes] = useState(vehicleMakes || []);
    const [isMakesLoading, setIsMakesLoading] = useState(false);
    const searchTimeout = React.useRef(null);

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Fetch vehicle models when make changes
    const fetchVehicleModels = async () => {
        try {
            const selectedMake = vehicleMakes.find((make) => make.label === selectedRow.make)
            const response = await getAllVehicleModels(accountId, selectedMake.makeId);
            if (response.success) {
                setVehicleModels(response.data.data || []);
            } else {
                setUpdateStatus({
                    message: response.data.message || 'Failed to fetch vehicle models',
                    type: 'error'
                });
                setVehicleModels([]);
            }
        } catch (error) {
            setUpdateStatus({
                message: 'An error occurred while fetching vehicle models',
                type: 'error'
            });
            setVehicleModels([]);
        }
    };

    useEffect(() => {
        if (selectedRow.make && !globalDataLoading) {
            fetchVehicleModels();
        } else {
            setVehicleModels([]);
        }
    }, [selectedRow.make, globalDataLoading]);

    const handleFieldUpdate = async (value, fieldName) => {
        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            vehicleId: 'vehicleId',
            vin: 'vin',
            name: 'name',
            licensePlate: 'licensePlate',
            make: 'make',
            model: 'model',
            year: 'year',
            vehicleType: 'vehicleType',
            bodyClass: 'bodyClass',
            fuelType: 'fuelType',
            status: 'status',
            color: 'attributes.color',
            note: 'note'
        };

        let payload;
        if (fieldName === 'color') {
            payload = { attributes: { ...selectedRow.attributes, color: value } };
        } else {
            payload = { [fieldMap[fieldName]]: value };
        }
        console.log(payload)

        try {
            const response = await updateVehicle(accountId, selectedRow.vehicleId, payload);
            if (response.success) {
                const updatedRow = { ...selectedRow, attributes: { ...selectedRow.attributes, color: value } };
                if (fieldName === 'make') {
                    // Clear model when make changes
                    updatedRow.model = '';
                    setVehicleModels([]);
                    await fetchVehicleModels();
                }
                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.vehicleId === selectedRow.vehicleId ? updatedRow : row
                ));
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
            } else {
                setUpdateStatus({
                    message: response.data.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
                // throw new Error('API update failed');
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
            throw error;
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const handleMakeSearch = async (inputValue) => {
        if (!inputValue) return;

        setIsMakesLoading(true);
        try {
            const response = await getAllVehicleMakes(accountId, inputValue);
            if (response.success) {
                setLocalMakes(response.data.data || []);
            }
        } catch (error) {
            console.error('Error searching makes:', error);
        } finally {
            setIsMakesLoading(false);
        }
    };

    const handleInputChange = (newValue, { action }) => {
        if (action === 'input-change') {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => {
                handleMakeSearch(newValue);
            }, 500);
        }
    };

    const handleMenuOpen = async () => {
        if (localMakes.length === 0) {
            setIsMakesLoading(true);
            try {
                const response = await getAllVehicleMakes(accountId);
                if (response.success) {
                    setLocalMakes(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching initial makes:', error);
            } finally {
                setIsMakesLoading(false);
            }
        }
    };

    const labelMap = {
        vehicleId: 'Vehicle ID',
        vin: 'VIN',
        name: 'Name',
        licensePlate: 'License Plate',
        make: 'Make',
        model: 'Model',
        year: 'Year',
        vehicleType: 'Vehicle Type',
        bodyClass: 'Body Class',
        fuelType: 'Fuel Type',
        status: 'Status',
        color: 'Color',
        note: 'Note'
    };


    const vehicleTypesOptions = prependDefault(vehicleTypes?.map(v => ({ value: v.slug, label: v.label })), 'Types');
    const vehicleBodiesOptions = prependDefault(vehicleBodies?.map(v => ({ value: v.slug, label: v.label })), 'Bodies');
    const vehicleMakesOptions = prependDefault(localMakes?.map(v => ({ value: v.label, label: v.label })), 'Makes');
    const vehicleModelsOptions = prependDefault(vehicleModels?.map(v => ({ value: v.label, label: v.label })), 'Models');

    const fuelTypeOptions = [
        { value: 'Gasoline', label: 'Gasoline' },
        { value: 'Diesel', label: 'Diesel' },
    ];

    return (
        <div className="space-y-3 px-4 pt-3 pb-4 relative">
            <Heading className="text-left mb-3">Vehicle Information</Heading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}>
                <RxCross2 className='text-2xl' />
            </button>

            {updateStatus.message && (
                <div className={`px-4 py-2 text-sm ${updateStatus.type === 'success' ? 'text-green-600 bg-green-100' :
                    updateStatus.type === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'} rounded-md mx-4`}>
                    {updateStatus.message}
                </div>
            )}
            {
                globalDataLoading ?
                    <div className='py-40 flex justify-center w-full'>
                        <Loader />
                    </div>
                    :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">

                        <EditableField
                            label="VIN"
                            name="vin"
                            value={selectedRow.vin || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.vin}
                        />

                        <EditableField
                            label="Name"
                            name="name"
                            value={selectedRow.name || ''}
                            onChange={handleFieldUpdate}
                            isEditable={false}
                            isLoading={loadingFields.name}
                        />

                        <EditableField
                            label="License Plate"
                            name="licensePlate"
                            value={selectedRow.licensePlate || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.licensePlate}
                        />

                        <EditableField
                            label="Make"
                            name="make"
                            value={camelCaseToWords(selectedRow.make) || ''}
                            onChange={handleFieldUpdate}
                            onInputChange={handleInputChange}
                            onMenuOpen={handleMenuOpen}
                            selectLoading={isMakesLoading}
                            type="select"
                            options={vehicleMakesOptions}
                            isLoading={loadingFields.make}
                        />

                        <EditableField
                            label="Model"
                            name="model"
                            value={camelCaseToWords(selectedRow.model) || ''}
                            onChange={handleFieldUpdate}
                            type="select"
                            options={vehicleModelsOptions}
                            isLoading={loadingFields.model}
                        />

                        <EditableField
                            label="Year"
                            name="year"
                            value={selectedRow.year || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.year}
                            type='number'
                        />

                        <EditableField
                            label="Vehicle Type"
                            name="vehicleType"
                            value={camelCaseToWords(selectedRow.vehicleType) || ''}
                            onChange={handleFieldUpdate}
                            type="select"
                            options={vehicleTypesOptions}
                            isLoading={loadingFields.vehicleType}
                        />

                        <EditableField
                            label="Body Class"
                            name="bodyClass"
                            value={selectedRow.bodyClass || ''}
                            onChange={handleFieldUpdate}
                            type="select"
                            options={vehicleBodiesOptions}
                            isLoading={loadingFields.bodyClass}
                        />

                        <EditableField
                            label="Color"
                            name="color"
                            value={selectedRow.attributes?.color || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.color}
                        />

                        <EditableField
                            label="Fuel Type"
                            name="fuelType"
                            value={camelCaseToWords(selectedRow.fuelType) || ''}
                            onChange={handleFieldUpdate}
                            type="select"
                            options={fuelTypeOptions}
                            isLoading={loadingFields.fuelType}
                        />

                        <EditableField
                            label="Status"
                            name="status"
                            value={camelCaseToWords(selectedRow.status)}
                            onChange={handleFieldUpdate}
                            type="select"
                            isEditable={false}
                            isLoading={loadingFields.status}
                        />

                        <EditableField
                            label="Note"
                            name="note"
                            type='textarea'
                            rows={2}
                            value={selectedRow?.note || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.note}
                        />
                    </div>
            }


        </div>
    );
};

export default EditModal;